import React, { useId } from "react"
import {
  TabsBaseScrollButtons,
  tabsBaseContainerStyles,
  tabsBaseListStyles,
  useTabsBaseOverflowScroll
} from "../tabs-base"
import type { TabsBaseOrientation } from "../tabs-base"
import { VisuallyHidden } from "../visually-hidden"
import { cn } from "../utils"
import { TabLinksContext } from "./tab-links-context"
import { TabLinksItem } from "./tab-links-item"

export type { TabLinksItemProps } from "./tab-links-item"

// =============================================================================
// Props
// =============================================================================

export type TabLinksProps = {
  /**
   * The accessible name for the navigation landmark. Differentiates it from
   * other `<nav>`s, such as the main `Navigation` (e.g. "Broadcast sections").
   */
  accessibleName: string
  children?: React.ReactNode
  className?: string
  /**
   * The consumer's current path (e.g. `window.location.pathname`). An item is
   * active when its `href` matches this exactly.
   */
  currentPath: string
  /** Whether the links sit in a row or in a column */
  orientation?: TabsBaseOrientation
}

// =============================================================================
// Component
// =============================================================================

/**
 * Tab links for cross-page navigation. Renders a `<nav>` landmark containing
 * `<a>` elements that *look* like tabs but move the user between pages (each
 * link has its own URL). The active link is derived from `currentPath`.
 *
 * Horizontal links scroll when they overflow and fade at the edges, where
 * a button scrolls them by a page for pointer users. Keyboard users reach every
 * link with Tab, so the buttons are hidden from assistive tech and the tab
 * order. The active link and a keyboard-focused link are scrolled clear of the
 * fades.
 *
 * Compose with `TabLinks.Item` for each link.
 *
 * For in-page panel switching (same URL, switching content), use `Tabs`
 * instead, which renders a real `role="tablist"` with arrow-key navigation.
 */
export function TabLinksRoot({
  accessibleName,
  children,
  className,
  currentPath,
  orientation = "horizontal"
}: TabLinksProps) {
  const accessibleNameId = useId()
  const { scrollerProps, scrollButtonsProps } =
    useTabsBaseOverflowScroll<HTMLElement>("aria-current", orientation)

  return (
    <TabLinksContext.Provider value={{ currentPath, orientation }}>
      <div
        className={cn(tabsBaseContainerStyles({ orientation }), className)}
        data-slot="tab-links"
      >
        <nav
          {...scrollerProps}
          className={cn(tabsBaseListStyles({ orientation }))}
          aria-labelledby={accessibleNameId}
          data-slot="tab-links-nav"
        >
          <VisuallyHidden id={accessibleNameId}>
            {accessibleName}
          </VisuallyHidden>
          {children}
        </nav>
        <TabsBaseScrollButtons
          {...scrollButtonsProps}
          data-slot="tab-links-scroll-button"
        />
      </div>
    </TabLinksContext.Provider>
  )
}

// =============================================================================
// Compound component
// =============================================================================

interface TabLinksComponent {
  (props: TabLinksProps): React.JSX.Element
  Item: typeof TabLinksItem
}

export const TabLinks: TabLinksComponent = Object.assign(TabLinksRoot, {
  Item: TabLinksItem
} as const)
